import { TestOrgNumberButton } from '../components/TestOrgNumberButton';
import { withTestOrgNumber } from '../utils/companyIdentifiers';
import { AddressAutocomplete, applySelectedAddress } from '../components/AddressAutocomplete';
import { PhoneInput } from '../components/PhoneInput';
import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout, Card, KycStatusPanel, TermsAcceptanceHistory } from '../components';
import type { KycStatusMessage } from '../components';
import { Breadcrumb } from '../components/Breadcrumb';
import { CompanyThemeEditor } from '../components/company-theme/CompanyThemeEditor';
import { AdminCompanyRegistration } from '../components/AdminCompanyRegistration';
import { adminApi, type AdyenAccount, type CompanyRegistrationInfo } from '../services/api';
import { apiErrorText } from '../utils/apiError';
import type { Company, AdminCustomer } from '../types';
import { bankNamesText } from '../components/CustomerBanks';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

type PersonSortField = 'customerNumber' | 'name' | 'email' | 'phone' | 'address' | 'postalCode' | 'city' | 'banks' | 'createdAt' | 'verifiedCustomer';

interface CompanyFormData {
  companyName: string;
  organizationNumber: string;
  phone: string;
  email: string;
  address: string;
  city: string;
  postalCode: string;
  country: string;
  vatNumber: string;
  verifiedCompany: string;
  website: string;
  companyPresentation: string;
  isPartner: boolean;
  experienceYears: string;
  specialties: string;
  languages: string;
  geographicArea: string;
  hourlyRate: string;
  responseTime: string;
  leadTime: string;
}

export function AdminCompanyView() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [company, setCompany] = useState<Company | null>(null);
  // Who registered the company (S3). Kept apart: the update response does not carry it.
  const [registration, setRegistration] = useState<CompanyRegistrationInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'persons' | 'kyc' | 'practical' | 'settings' | 'theme'>('overview');
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // KYC (Kundkännedom) — read-only Adyen status for this company (admin view)
  const [adyenAccount, setAdyenAccount] = useState<AdyenAccount | null>(null);
  const [adyenLoading, setAdyenLoading] = useState(false);
  const [adyenLoaded, setAdyenLoaded] = useState(false);
  const [adyenRefreshing, setAdyenRefreshing] = useState(false);
  const [adyenMessage, setAdyenMessage] = useState<KycStatusMessage | null>(null);

  // Connected private persons (customers belonging to this company)
  const [persons, setPersons] = useState<AdminCustomer[]>([]);
  const [personsLoading, setPersonsLoading] = useState(false);
  const [personsLoaded, setPersonsLoaded] = useState(false);
  const [personFilters, setPersonFilters] = useState<Record<string, string>>({});
  const [personSortField, setPersonSortField] = useState<PersonSortField>('customerNumber');
  const [personSortDir, setPersonSortDir] = useState<'asc' | 'desc'>('asc');
  const [formData, setFormData] = useState<CompanyFormData>({
    companyName: '',
    organizationNumber: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    postalCode: '',
    country: '',
    vatNumber: '',
    verifiedCompany: '',
    website: '',
    companyPresentation: '',
    isPartner: false,
    experienceYears: '',
    specialties: '',
    languages: '',
    geographicArea: '',
    hourlyRate: '',
    responseTime: '',
    leadTime: ''
  });

  const basePath = '/admin/company-list';

  useEffect(() => {
    if (id) {
      loadCompany(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadCompany = async (companyId: string) => {
    try {
      setIsLoading(true);
      const found = await adminApi.getCompany(companyId);
      if (found) {
        setCompany(found);
        setRegistration(found.registration ?? null);
        setFormData({
          companyName: found.companyName || '',
          organizationNumber: found.organizationNumber || '',
          phone: found.phone || '',
          email: found.email || '',
          address: found.address || '',
          city: found.city || '',
          postalCode: found.postalCode || '',
          country: found.country || '',
          vatNumber: found.vatNumber || '',
          verifiedCompany: found.verifiedCompany || '',
          website: found.website || '',
          companyPresentation: found.companyPresentation || '',
          isPartner: found.isPartner || false,
          experienceYears: String(found.experienceYears || ''),
          specialties: (found.specialties || []).join(', '),
          languages: (found.languages || []).join(', '),
          geographicArea: found.geographicArea || '',
          hourlyRate: found.hourlyRate || '',
          responseTime: found.responseTime || '',
          leadTime: found.leadTime || ''
        });
      }
    } catch (err) {
      setMessage({ type: 'error', text: t('common.error') });
    } finally {
      setIsLoading(false);
    }
  };

  const loadAdyenAccount = async (companyId: string, options?: { refresh?: boolean }) => {
    const setBusy = options?.refresh ? setAdyenRefreshing : setAdyenLoading;
    setBusy(true);
    if (options?.refresh) setAdyenMessage(null);
    try {
      const account = options?.refresh
        ? await adminApi.refreshCompanyAdyenAccount(companyId)
        : await adminApi.getCompanyAdyenAccount(companyId);
      setAdyenAccount(account);
      setAdyenLoaded(true);
      if (options?.refresh) {
        setAdyenMessage({ type: 'success', text: t('adminCompanyView.adyenStatusUpdated') });
      }
    } catch (error) {
      setAdyenMessage({
        type: 'error',
        text: apiErrorText(t, error, 'adminCompanyView.kycStatusError')
      });
    } finally {
      setBusy(false);
    }
  };

  // Lazily load KYC status the first time the tab is opened
  useEffect(() => {
    if (activeTab !== 'kyc' || !company || adyenLoaded) return;
    void loadAdyenAccount(company.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, company, adyenLoaded]);

  // Lazily load connected persons the first time the tab is opened
  useEffect(() => {
    if (activeTab !== 'persons' || !company || personsLoaded) return;
    const loadPersons = async () => {
      try {
        setPersonsLoading(true);
        // Only this contractor's clients, never a cut-off list (ClickUp 869fjkyz1).
        setPersons(await adminApi.getAllCustomers({ companyNumber: company.companyNumber }));
        setPersonsLoaded(true);
      } catch (err) {
        setMessage({ type: 'error', text: t('common.error') });
      } finally {
        setPersonsLoading(false);
      }
    };
    loadPersons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, company, personsLoaded]);

  const handlePersonSort = (field: PersonSortField) => {
    if (personSortField === field) {
      setPersonSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setPersonSortField(field);
      setPersonSortDir('asc');
    }
  };

  const handlePersonFilterChange = (field: string, value: string) => {
    setPersonFilters((prev) => ({ ...prev, [field]: value }));
  };

  const renderPersonSortIcon = (field: PersonSortField) => {
    if (personSortField !== field) {
      return <i className="feather-chevrons-up-down ms-1 opacity-25"></i>;
    }
    return personSortDir === 'asc' ? (
      <i className="feather-chevron-up ms-1"></i>
    ) : (
      <i className="feather-chevron-down ms-1"></i>
    );
  };

  const personColumns: { field: PersonSortField; label: string }[] = [
    { field: 'customerNumber', label: t('customers.columns.customerNumber') },
    { field: 'name', label: t('customers.columns.name') },
    { field: 'email', label: t('customers.columns.email') },
    { field: 'phone', label: t('customers.columns.phone') },
    { field: 'address', label: t('customers.columns.address') },
    { field: 'postalCode', label: t('customers.columns.postalCode') },
    { field: 'city', label: t('customers.columns.city') },
    // The Beställare's banks (ClickUp 869eu6fpm).
    { field: 'banks', label: t('customers.columns.banks') },
    { field: 'createdAt', label: t('adminCustomers.columns.createdAt') },
    { field: 'verifiedCustomer', label: t('customers.columns.verified') }
  ];

  const personVerification = (p: AdminCustomer) =>
    p.verifiedCustomer === 'Verifierad'
      ? { label: t('customers.status.verified'), className: 'bg-success' }
      : { label: t('customers.status.shared'), className: 'bg-warning text-dark' };

  // The banks are compared and shown as their names.
  const personValue = (p: AdminCustomer, field: string): unknown =>
    field === 'banks' ? bankNamesText(t, p.banks) : (p as any)[field];

  const visiblePersons = useMemo(() => {
    let result = [...persons];

    Object.entries(personFilters).forEach(([field, value]) => {
      if (value) {
        result = result.filter((p) => {
          const fieldValue = personValue(p, field);
          if (field === 'verifiedCustomer') {
            return String(fieldValue) === value;
          }
          return String(fieldValue || '').toLowerCase().includes(value.toLowerCase());
        });
      }
    });

    result.sort((a, b) => {
      const aValue = personValue(a, personSortField);
      const bValue = personValue(b, personSortField);
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return personSortDir === 'asc' ? aValue - bValue : bValue - aValue;
      }
      const aStr = String(aValue || '').toLowerCase();
      const bStr = String(bValue || '').toLowerCase();
      return personSortDir === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
    });

    return result;
    // personValue only depends on the language (t).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persons, personFilters, personSortField, personSortDir, t]);

  const exportPersonsToCsv = () => {
    const headers = personColumns.map((c) => c.label);
    const getCell = (p: AdminCustomer, field: PersonSortField): string | number => {
      if (field === 'createdAt') return formatDate(p.createdAt);
      if (field === 'verifiedCustomer') {
        return p.verifiedCustomer === 'Verifierad'
          ? t('customers.status.verified')
          : t('customers.status.shared');
      }
      return (personValue(p, field) as string | number | undefined) ?? '';
    };
    const rows = visiblePersons.map((p) => personColumns.map((c) => getCell(p, c.field)));
    const csvContent = [
      headers.join(';'),
      ...rows.map((row) => row.map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(';'))
    ].join('\n');
    const BOM = '﻿';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `company_${company?.companyNumber || ''}_persons_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const handleEdit = () => {
    setIsEditing(true);
    setMessage(null);
  };

  const handleCancelEdit = () => {
    if (company) {
      setFormData({
        companyName: company.companyName || '',
        organizationNumber: company.organizationNumber || '',
        phone: company.phone || '',
        email: company.email || '',
        address: company.address || '',
        city: company.city || '',
        postalCode: company.postalCode || '',
        country: company.country || '',
        vatNumber: company.vatNumber || '',
        verifiedCompany: company.verifiedCompany || '',
        website: company.website || '',
        companyPresentation: company.companyPresentation || '',
        isPartner: company.isPartner || false,
        experienceYears: String(company.experienceYears || ''),
        specialties: (company.specialties || []).join(', '),
        languages: (company.languages || []).join(', '),
        geographicArea: company.geographicArea || '',
        hourlyRate: company.hourlyRate || '',
        responseTime: company.responseTime || '',
        leadTime: company.leadTime || ''
      });
    }
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!id || !company) return;
    try {
      setIsSaving(true);
      // The status has its own endpoint: admin can only block or lift a block;
      // Verifierad comes from the contractor completing onboarding.
      const { verifiedCompany, ...details } = formData;
      const wasBlocked = company.verifiedCompany === 'Blockerad';
      const blocked = verifiedCompany === 'Blockerad';
      let updated = await adminApi.updateCompany(id, {
        ...details,
        experienceYears: Number(formData.experienceYears) || 0,
        specialties: formData.specialties ? formData.specialties.split(',').map(s => s.trim()).filter(Boolean) : [],
        languages: formData.languages ? formData.languages.split(',').map(s => s.trim()).filter(Boolean) : [],
      } as any);
      if (blocked !== wasBlocked) {
        await adminApi.setCompanyVerification(id, blocked ? 'Blockerad' : 'Ovärifierad');
        updated = (await adminApi.getCompany(id)) ?? updated;
      }
      setCompany(updated);
      setFormData(prev => ({ ...prev, verifiedCompany: updated.verifiedCompany || '' }));
      setIsEditing(false);
      setMessage({ type: 'success', text: t('common.saved') });
    } catch (err) {
      setMessage({ type: 'error', text: t('common.error') });
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (field: keyof CompanyFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('sv-SE');
    } catch {
      return dateStr;
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!company) {
    return (
      <DashboardLayout>
        <div className="alert alert-danger">{t('common.error')}</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Page Header */}
      <div className="page-header mb-4">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="fw-bold mb-0">
              {t('adminCompanyView.title')} - {company.companyNumber}
            </h5>
          </div>
          <Breadcrumb items={[
            { label: t('nav.home'), path: '/home' },
            { label: t('adminCompanyView.title'), path: basePath },
            { label: company.companyName }
          ]} />
        </div>
        <div className="page-header-right ms-auto d-none d-sm-block">
          <p className="fw-bold text-muted mb-0">{t('adminCompanyView.description')}</p>
        </div>
        <div className="d-flex justify-content-end mt-2">
          <button className="btn btn-light-brand" onClick={() => navigate(basePath)}>
            <i className="feather-arrow-left me-2"></i>
            {t('common.back')}
          </button>
        </div>
      </div>

      {message && (
        <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show`}>
          {message.text}
          <button type="button" className="btn-close" onClick={() => setMessage(null)}></button>
        </div>
      )}

      {/* Tabs Navigation */}
      <div className="mb-4" style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <ul className="nav nav-tabs nav-tabs-custom-style flex-nowrap" role="tablist" style={{ minWidth: 'max-content' }}>
          <li className="nav-item" role="presentation">
            <button className={`nav-link px-3 ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => setActiveTab('overview')}>
              <i className="feather-briefcase d-sm-none me-0"></i>
              <span className="d-none d-sm-inline">{t('adminCompanyView.tab.overview')}</span>
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button className={`nav-link px-3 ${activeTab === 'persons' ? 'active' : ''}`} onClick={() => setActiveTab('persons')}>
              <i className="feather-users d-sm-none me-0"></i>
              <span className="d-none d-sm-inline">{t('adminCompanyView.tab.persons', 'Beställare')}</span>
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button className={`nav-link px-3 ${activeTab === 'kyc' ? 'active' : ''}`} onClick={() => setActiveTab('kyc')}>
              <i className="feather-shield d-sm-none me-0"></i>
              <span className="d-none d-sm-inline">{t('adminCompanyView.tab.kyc', 'Kundkännedom (KYC)')}</span>
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button className={`nav-link px-3 ${activeTab === 'practical' ? 'active' : ''}`} onClick={() => setActiveTab('practical')}>
              <i className="feather-info d-sm-none me-0"></i>
              <span className="d-none d-sm-inline">{t('marketplace.provider.practicalInfo', 'Praktisk information')}</span>
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button className={`nav-link px-3 ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => setActiveTab('settings')}>
              <i className="feather-settings d-sm-none me-0"></i>
              <span className="d-none d-sm-inline">{t('adminCompanyView.tab.settings', 'Inställningar')}</span>
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button
              className={`nav-link px-3 ${activeTab === 'theme' ? 'active' : ''}`}
              onClick={() => setActiveTab('theme')}
              aria-label={t('adminCompanyView.tab.theme')}
            >
              <i className="feather-droplet d-sm-none me-0" aria-hidden="true"></i>
              <span className="d-none d-sm-inline">{t('adminCompanyView.tab.theme')}</span>
            </button>
          </li>
        </ul>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div>
          <Card>
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
              <h5 className="card-title mb-0">{t('adminCompanyView.tab.overview')}</h5>
              {!isEditing && (
                <button type="button" className="btn btn-primary" onClick={handleEdit} aria-label={t('common.change', 'Ändra')}>
                  <i className="feather-edit me-2"></i>{t('common.change', 'Ändra')}
                </button>
              )}
            </div>
            <div className="card-body">
              {/* === Grunduppgifter === */}
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">{t('adminCompanyList.columns.companyNumber')}</label>
                  <input type="text" className="form-control" value={company.companyNumber ?? ''} disabled />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">{t('adminCompanyList.columns.verifiedCompany')}</label>
                  <div data-testid="admin-company-view-status">
                    <span className={`badge bg-${company.verifiedCompany === 'Verifierad' ? 'success' : company.verifiedCompany === 'Blockerad' ? 'danger' : 'secondary'}`}>
                      {company.verifiedCompany === 'Verifierad'
                        ? t('adminCompanyList.status.verified')
                        : company.verifiedCompany === 'Blockerad'
                          ? t('adminCompanyList.status.blocked')
                          : t('adminCompanyList.status.unverified')}
                    </span>
                  </div>
                  <div className="form-text">{t('adminCompanyList.form.verifiedHelp')}</div>
                  {isEditing && (
                    <div className="form-check form-switch admin-company-block-toggle mt-2">
                      <input
                        id="adminCompanyViewBlocked"
                        type="checkbox"
                        role="switch"
                        className="form-check-input"
                        checked={formData.verifiedCompany === 'Blockerad'}
                        onChange={(e) => handleChange('verifiedCompany', e.target.checked ? 'Blockerad' : (company.verifiedCompany === 'Blockerad' ? 'Ovärifierad' : company.verifiedCompany || 'Ovärifierad'))}
                      />
                      <label className="form-check-label" htmlFor="adminCompanyViewBlocked">
                        {t('adminCompanyList.form.blocked')}
                      </label>
                    </div>
                  )}
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">{t('adminCompanyList.columns.companyName')}</label>
                  <input type="text" className="form-control" value={isEditing ? formData.companyName : (company.companyName || '')} disabled={!isEditing} onChange={(e) => handleChange('companyName', e.target.value)} />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">{t('adminCompanyList.columns.organizationNumber')}</label>
                  <input type="text" className="form-control" value={isEditing ? formData.organizationNumber : (company.organizationNumber || '')} disabled={!isEditing} onChange={(e) => handleChange('organizationNumber', e.target.value)} />
                  <TestOrgNumberButton disabled={!isEditing} onGenerated={value => setFormData(previous => withTestOrgNumber(previous, value))} />
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">{t('adminCompanyList.columns.phone')}</label>
                  <PhoneInput name="phone" className="form-control" value={isEditing ? formData.phone : (company.phone || '')} disabled={!isEditing} onChange={(e) => handleChange('phone', e.target.value)} />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">{t('adminCompanyList.form.vatNumber')}</label>
                  <input type="text" className="form-control" value={isEditing ? formData.vatNumber : (company.vatNumber || '')} disabled={!isEditing} onChange={(e) => handleChange('vatNumber', e.target.value)} />
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">{t('adminCompanyList.columns.email')}</label>
                  <input type="email" className="form-control" value={isEditing ? formData.email : (company.email || '')} disabled={!isEditing} onChange={(e) => handleChange('email', e.target.value)} />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">{t('adminCompanyList.form.website')}</label>
                  <input type="text" className="form-control" value={isEditing ? formData.website : (company.website || '')} disabled={!isEditing} onChange={(e) => handleChange('website', e.target.value)} />
                </div>
              </div>

              {/* === Adress === */}
              <hr className="my-3" />
              <h6 className="fw-bold mb-3">{t('adminCompanyList.form.address', 'Adress')}</h6>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <AddressAutocomplete
                    name="address"
                    label={t('adminCompanyList.form.address')}
                    onAddressSelect={address => setFormData(previous => applySelectedAddress(previous, address))}
                    type="text"
                    value={isEditing ? formData.address : (company.address || '')}
                    disabled={!isEditing}
                    onChange={(e) => handleChange('address', e.target.value)}
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">{t('adminCompanyList.form.postalCode')}</label>
                  <input type="text" className="form-control" value={isEditing ? formData.postalCode : (company.postalCode || '')} disabled={!isEditing} onChange={(e) => handleChange('postalCode', e.target.value)} />
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">{t('adminCompanyList.form.city')}</label>
                  <input type="text" className="form-control" value={isEditing ? formData.city : (company.city || '')} disabled={!isEditing} onChange={(e) => handleChange('city', e.target.value)} />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">{t('adminCompanyList.form.country')}</label>
                  <input type="text" className="form-control" value={isEditing ? formData.country : (company.country || '')} disabled={!isEditing} onChange={(e) => handleChange('country', e.target.value)} />
                </div>
              </div>

              {/* === Datum === */}
              <hr className="my-3" />
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">{t('adminCompanyList.columns.createdAt')}</label>
                  <input type="text" className="form-control" value={formatDate(company.createdAt)} disabled />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">{t('adminCompanyList.columns.updatedAt')}</label>
                  <input type="text" className="form-control" value={formatDate(company.updatedAt)} disabled />
                </div>
              </div>

              <hr className="my-3" />
              <AdminCompanyRegistration registration={registration} />

              {/* Action Buttons */}
              <div className="d-flex justify-content-end gap-2 mt-4">
                {isEditing ? (
                  <>
                    <button className="btn btn-secondary" onClick={handleCancelEdit} disabled={isSaving}>
                      {t('common.cancel')}
                    </button>
                    <button className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
                      {isSaving && <span className="spinner-border spinner-border-sm me-2" role="status"></span>}
                      {t('common.save')}
                    </button>
                  </>
                ) : (
                  <button className="btn btn-secondary" onClick={() => navigate(basePath)}>
                    {t('common.cancel')}
                  </button>
                )}
              </div>
            </div>
          </Card>
          {company && <TermsAcceptanceHistory userId={company.userId ?? null} />}
        </div>
      )}

      {/* Connected Private Persons Tab */}
      {activeTab === 'persons' && (
        <div>
          <Card>
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
              <h5 className="card-title mb-0">
                {t('adminCompanyView.tab.persons', 'Beställare')}
                {personsLoaded && (
                  <span className="text-muted fw-normal ms-2">({visiblePersons.length})</span>
                )}
              </h5>
              <button
                className="btn btn-light-brand btn-sm"
                onClick={exportPersonsToCsv}
                disabled={visiblePersons.length === 0}
              >
                <i className="feather-download"></i>
                <span className="d-none d-sm-inline ms-2">{t('customers.export', 'Exportera')}</span>
              </button>
            </div>
            <div className="card-body">
              {personsLoading ? (
                <div className="d-flex justify-content-center align-items-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">{t('common.loading')}</span>
                  </div>
                </div>
              ) : (
                <>
                  {/* Desktop: Table view */}
                  <div className="d-none d-md-block">
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            {personColumns.map((col) => (
                              <th
                                key={col.field}
                                onClick={() => handlePersonSort(col.field)}
                                style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                              >
                                {col.label}
                                {renderPersonSortIcon(col.field)}
                              </th>
                            ))}
                          </tr>
                          <tr>
                            {personColumns.map((col) => (
                              <th key={`filter-${col.field}`} className="p-1">
                                {col.field === 'verifiedCustomer' ? (
                                  <select
                                    className="form-select form-select-sm"
                                    value={personFilters[col.field] || ''}
                                    onChange={(e) => handlePersonFilterChange(col.field, e.target.value)}
                                  >
                                    <option value="">{t('customers.allStatuses')}</option>
                                    <option value="Delad">{t('customers.status.shared')}</option>
                                    <option value="Verifierad">{t('customers.status.verified')}</option>
                                  </select>
                                ) : (
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder={t('customers.search')}
                                    value={personFilters[col.field] || ''}
                                    onChange={(e) => handlePersonFilterChange(col.field, e.target.value)}
                                  />
                                )}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {visiblePersons.length === 0 ? (
                            <tr>
                              <td colSpan={personColumns.length} className="text-center text-muted py-4">
                                {persons.length === 0 ? t('customers.noCustomers', 'Inga beställare ännu') : t('projects.noResults')}
                              </td>
                            </tr>
                          ) : (
                            visiblePersons.map((p) => {
                              const v = personVerification(p);
                              return (
                                <tr
                                  key={p.id}
                                  onDoubleClick={() => navigate(`/admin/customers/${p.id}`)}
                                  style={{ cursor: 'pointer' }}
                                  title={t('customers.viewCustomer', 'Visa')}
                                >
                                  {personColumns.map((col) => {
                                    if (col.field === 'verifiedCustomer') {
                                      return (
                                        <td key={col.field}>
                                          <span className={`badge ${v.className}`}>{v.label}</span>
                                        </td>
                                      );
                                    }
                                    if (col.field === 'createdAt') {
                                      return <td key={col.field}>{formatDate(p.createdAt)}</td>;
                                    }
                                    return <td key={col.field}>{personValue(p, col.field) as React.ReactNode}</td>;
                                  })}
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Mobile: Card view */}
                  <div className="d-md-none">
                    <div className="mb-3">
                      <div className="row g-2">
                        <div className="col-8">
                          <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder={t('customers.search')}
                            value={personFilters['name'] || ''}
                            onChange={(e) => handlePersonFilterChange('name', e.target.value)}
                          />
                        </div>
                        <div className="col-4">
                          <select
                            className="form-select form-select-sm"
                            value={personFilters['verifiedCustomer'] || ''}
                            onChange={(e) => handlePersonFilterChange('verifiedCustomer', e.target.value)}
                          >
                            <option value="">{t('customers.allStatuses')}</option>
                            <option value="Delad">{t('customers.status.shared')}</option>
                            <option value="Verifierad">{t('customers.status.verified')}</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    {visiblePersons.length === 0 ? (
                      <p className="text-muted text-center py-3">
                        {persons.length === 0 ? t('customers.noCustomers', 'Inga beställare ännu') : t('projects.noResults')}
                      </p>
                    ) : (
                      visiblePersons.map((p) => {
                        const v = personVerification(p);
                        return (
                          <div
                            key={p.id}
                            className="card mb-2"
                            onClick={() => navigate(`/admin/customers/${p.id}`)}
                            style={{ cursor: 'pointer' }}
                          >
                            <div className="card-body py-2 px-3">
                              <div className="d-flex align-items-center gap-2 mb-1">
                                <span className="fw-semibold text-truncate">
                                  #{p.customerNumber} {p.name}
                                </span>
                                <span className={`badge ${v.className} flex-shrink-0`}>{v.label}</span>
                              </div>
                              {p.email && (
                                <div className="text-muted small text-truncate">
                                  <i className="feather-mail me-1"></i>{p.email}
                                </div>
                              )}
                              {p.phone && (
                                <div className="text-muted small">
                                  <i className="feather-phone me-1"></i>{p.phone}
                                </div>
                              )}
                              {(p.address || p.city) && (
                                <div className="text-muted small text-truncate">
                                  <i className="feather-map-pin me-1"></i>
                                  {[p.address, p.postalCode, p.city].filter(Boolean).join(', ')}
                                </div>
                              )}
                              {Boolean(p.banks?.length) && (
                                <div className="text-muted small">
                                  <i className="feather-credit-card me-1"></i>{bankNamesText(t, p.banks)}
                                </div>
                              )}
                              <div className="text-muted small mt-1">
                                <i className="feather-calendar me-1"></i>{formatDate(p.createdAt)}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div>
          <Card>
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
              <h5 className="card-title mb-0">{t('adminCompanyView.tab.settings', 'Inställningar')}</h5>
              {!isEditing && (
                <button type="button" className="btn btn-primary" onClick={handleEdit} aria-label={t('common.change', 'Ändra')}>
                  <i className="feather-edit me-2"></i>{t('common.change', 'Ändra')}
                </button>
              )}
            </div>
            <div className="card-body">
              <h6 className="fw-bold mb-3">{t('adminCompanyView.settingsSection.contractConfig', 'Avtalskonfiguration')}</h6>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">{t('adminCompanyView.isPartner', 'B2B Partner')}</label>
                  {isEditing ? (
                    <div className="form-check mt-2">
                      <input type="checkbox" className="form-check-input" id="isPartner" checked={formData.isPartner} onChange={(e) => handleChange('isPartner', e.target.checked as any)} />
                      <label className="form-check-label" htmlFor="isPartner">
                        {formData.isPartner ? t('adminCompanyView.partnerYes', 'Ja — Årsabonnemang (4 950 kr/år)') : t('adminCompanyView.partnerNo', 'Nej — Standardavtal (B2C)')}
                      </label>
                    </div>
                  ) : (
                    <div>
                      <span className={`badge bg-${company.isPartner ? 'primary' : 'secondary'}`}>
                        {company.isPartner ? t('adminCompanyView.partnerYes', 'B2B Partner') : t('adminCompanyView.partnerNo', 'B2C Standard')}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {!isEditing && (
                <div className="alert alert-light border py-2 px-3 mt-2">
                  <small className="text-muted">
                    <i className="feather-info me-1"></i>
                    {company.isPartner
                      ? t('adminCompanyView.partnerInfo', 'Hantverkaren har årsabonnemang. Beställaren betalar ingen avgift — hantverkaren bjuder.')
                      : t('adminCompanyView.standardInfo', 'Standardavtal (B2C). Båda parter delar på transaktionsavgiften.')
                    }
                  </small>
                </div>
              )}

              {/* Action Buttons (Spara/Avbryt endast i redigeringsläge) */}
              {isEditing && (
                <div className="d-flex justify-content-end gap-2 mt-4">
                  <button className="btn btn-secondary" onClick={handleCancelEdit} disabled={isSaving}>
                    {t('common.cancel')}
                  </button>
                  <button className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
                    {isSaving && <span className="spinner-border spinner-border-sm me-2" role="status"></span>}
                    {t('common.save')}
                  </button>
                </div>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* Practical Tab */}
      {activeTab === 'practical' && (
        <div>
          <Card>
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
              <h5 className="card-title mb-0">{t('marketplace.provider.practicalInfo', 'Praktisk information')}</h5>
              {!isEditing && (
                <button type="button" className="btn btn-primary" onClick={handleEdit} aria-label={t('common.change', 'Ändra')}>
                  <i className="feather-edit me-2"></i>{t('common.change', 'Ändra')}
                </button>
              )}
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">{t('marketplace.provider.experience', 'Erfarenhetsår')}</label>
                  <input type="number" className="form-control" value={isEditing ? formData.experienceYears : (company.experienceYears ?? 0)} disabled={!isEditing} onChange={e => handleChange('experienceYears', e.target.value as any)} />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">{t('marketplace.provider.geographicArea', 'Geografiskt område')}</label>
                  <input type="text" className="form-control" value={isEditing ? formData.geographicArea : (company.geographicArea || '')} disabled={!isEditing} onChange={e => handleChange('geographicArea', e.target.value as any)} placeholder={t('adminCompanyView.placeholderGeographicArea')} />
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">{t('marketplace.provider.specialties', 'Specialistområden')}</label>
                  {isEditing ? (
                    <>
                      <input type="text" className="form-control" value={formData.specialties} onChange={e => handleChange('specialties', e.target.value as any)} placeholder={t('adminCompanyView.placeholderSpecialties')} />
                      <small className="text-muted">{t('common.commaSeparated', 'Kommaseparerade värden')}</small>
                    </>
                  ) : (
                    <div>{(company.specialties || []).length > 0 ? (company.specialties || []).map((s: string, i: number) => <span key={i} className="badge bg-primary me-1 mb-1">{s}</span>) : <span className="text-muted">—</span>}</div>
                  )}
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">{t('marketplace.provider.languages', 'Språk')}</label>
                  {isEditing ? (
                    <>
                      <input type="text" className="form-control" value={formData.languages} onChange={e => handleChange('languages', e.target.value as any)} placeholder={t('adminCompanyView.placeholderLanguages')} />
                      <small className="text-muted">{t('common.commaSeparated', 'Kommaseparerade värden')}</small>
                    </>
                  ) : (
                    <div>{(company.languages || []).length > 0 ? (company.languages || []).map((l: string, i: number) => <span key={i} className="badge bg-info me-1 mb-1">{l}</span>) : <span className="text-muted">—</span>}</div>
                  )}
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">{t('marketplace.provider.hourlyRate', 'Timarvode')}</label>
                  <input type="text" className="form-control" value={isEditing ? formData.hourlyRate : (company.hourlyRate || '')} disabled={!isEditing} onChange={e => handleChange('hourlyRate', e.target.value as any)} placeholder={t('adminCompanyView.placeholderHourlyRate')} />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">{t('marketplace.provider.responseTime', 'Svarstid')}</label>
                  <input type="text" className="form-control" value={isEditing ? formData.responseTime : (company.responseTime || '')} disabled={!isEditing} onChange={e => handleChange('responseTime', e.target.value as any)} placeholder={t('adminCompanyView.placeholderResponseTime')} />
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">{t('marketplace.provider.leadTime', 'Ledtid')}</label>
                  <input type="text" className="form-control" value={isEditing ? formData.leadTime : (company.leadTime || '')} disabled={!isEditing} onChange={e => handleChange('leadTime', e.target.value as any)} placeholder={t('adminCompanyView.placeholderLeadTime')} />
                </div>
              </div>

              {/* === Företagspresentation === */}
              <hr className="my-3" />
              <div className="mb-3">
                <label className="form-label">{t('adminCompanyList.form.companyPresentation')}</label>
                {isEditing ? (
                  <>
                    <ReactQuill theme="snow" value={formData.companyPresentation} onChange={(v) => handleChange('companyPresentation', v as any)} style={{ height: '150px' }} />
                    <div style={{ height: '45px' }}></div>
                  </>
                ) : company.companyPresentation ? (
                  <div className="form-control bg-light" style={{ minHeight: '60px', height: 'auto' }}>
                    <div className="ql-editor p-0" dangerouslySetInnerHTML={{ __html: company.companyPresentation }} />
                  </div>
                ) : (
                  <input type="text" className="form-control" value="" disabled />
                )}
              </div>

              {/* Action Buttons (Spara/Avbryt endast i redigeringsläge) */}
              {isEditing && (
                <div className="d-flex justify-content-end gap-2 mt-4">
                  <button className="btn btn-secondary" onClick={handleCancelEdit} disabled={isSaving}>{t('common.cancel')}</button>
                  <button className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
                    {isSaving && <span className="spinner-border spinner-border-sm me-2" role="status"></span>}
                    {t('common.save')}
                  </button>
                </div>
              )}

              {/* === Hur era kunder ser er information === */}
              <hr className="my-4" />
              <h6 className="fw-bold mb-3"><i className="feather-eye me-2"></i>{t('settings.companyDetails.customerPreview', 'Hur era beställare ser er information')}</h6>
              <p className="text-muted small mb-3">{t('settings.companyDetails.customerPreviewDescription', 'Nedan visas hur beställare ser ert företag på sin startsida.')}</p>

              <div className="card border">
                <div className="card-body">
                  {/* Header */}
                  <div className="d-flex align-items-start gap-3 mb-3">
                    <div className="avatar-text rounded-circle bg-primary text-white d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: 56, height: 56, fontSize: 18, fontWeight: 600 }}>
                      {(company.companyName || '').split(' ').map((n: string) => n[0]).join('').substring(0, 2).toUpperCase()}
                    </div>
                    <div className="flex-grow-1">
                      <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                        <span className="fw-bold fs-6">{company.companyName}</span>
                        <span className="badge bg-success"><i className="feather-check me-1" style={{ fontSize: 10 }}></i>{t('personHome.yourContractor', 'Din hantverkare')}</span>
                      </div>
                    </div>
                  </div>

                  {/* Contact info */}
                  {(company.phone || company.email || company.website) && (
                    <div className="d-flex flex-wrap gap-3 mb-3 small">
                      {company.phone && (
                        <span className="text-muted"><i className="feather-phone me-1" style={{ fontSize: 13 }}></i>{company.phone}</span>
                      )}
                      {company.email && (
                        <span className="text-muted"><i className="feather-mail me-1" style={{ fontSize: 13 }}></i>{company.email}</span>
                      )}
                      {company.website && (
                        <span className="text-muted"><i className="feather-globe me-1" style={{ fontSize: 13 }}></i>{company.website}</span>
                      )}
                    </div>
                  )}

                  {/* Specialties */}
                  {(company.specialties || []).length > 0 && (
                    <div className="mb-3">
                      <small className="fw-semibold text-muted d-block mb-1">{t('marketplace.provider.specialties', 'Specialistområden')}</small>
                      <div className="d-flex flex-wrap gap-1">
                        {(company.specialties || []).map((s: string, i: number) => (
                          <span key={i} className="badge bg-light text-dark border">{s}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Practical info grid */}
                  {((company.experienceYears || 0) > 0 || company.hourlyRate || company.geographicArea || company.responseTime || company.leadTime || (company.languages || []).length > 0) && (
                    <div className="bg-light rounded p-3 mb-3">
                      <small className="fw-semibold text-muted d-block mb-2">{t('marketplace.provider.practicalInfo', 'Praktisk information')}</small>
                      <div className="row g-2">
                        {(company.experienceYears || 0) > 0 && (
                          <div className="col-6 col-md-4">
                            <div className="d-flex align-items-center gap-2">
                              <i className="feather-award text-muted" style={{ fontSize: 14 }}></i>
                              <div>
                                <div style={{ fontSize: 11 }} className="text-muted">{t('marketplace.provider.experience', 'Erfarenhet')}</div>
                                <div className="fw-semibold small">{company.experienceYears} {t('adminCompanyView.yearsSuffix')}</div>
                              </div>
                            </div>
                          </div>
                        )}
                        {company.hourlyRate && (
                          <div className="col-6 col-md-4">
                            <div className="d-flex align-items-center gap-2">
                              <i className="feather-tag text-muted" style={{ fontSize: 14 }}></i>
                              <div>
                                <div style={{ fontSize: 11 }} className="text-muted">{t('marketplace.provider.hourlyRate', 'Timarvode')}</div>
                                <div className="fw-semibold small">{company.hourlyRate}</div>
                              </div>
                            </div>
                          </div>
                        )}
                        {company.responseTime && (
                          <div className="col-6 col-md-4">
                            <div className="d-flex align-items-center gap-2">
                              <i className="feather-clock text-muted" style={{ fontSize: 14 }}></i>
                              <div>
                                <div style={{ fontSize: 11 }} className="text-muted">{t('marketplace.provider.responseTime', 'Svarstid')}</div>
                                <div className="fw-semibold small">{company.responseTime}</div>
                              </div>
                            </div>
                          </div>
                        )}
                        {company.geographicArea && (
                          <div className="col-6 col-md-4">
                            <div className="d-flex align-items-center gap-2">
                              <i className="feather-map-pin text-muted" style={{ fontSize: 14 }}></i>
                              <div>
                                <div style={{ fontSize: 11 }} className="text-muted">{t('marketplace.provider.geographicArea', 'Område')}</div>
                                <div className="fw-semibold small">{company.geographicArea}</div>
                              </div>
                            </div>
                          </div>
                        )}
                        {(company.languages || []).length > 0 && (
                          <div className="col-6 col-md-4">
                            <div className="d-flex align-items-center gap-2">
                              <i className="feather-globe text-muted" style={{ fontSize: 14 }}></i>
                              <div>
                                <div style={{ fontSize: 11 }} className="text-muted">{t('marketplace.provider.languages', 'Språk')}</div>
                                <div className="fw-semibold small">{(company.languages || []).join(', ')}</div>
                              </div>
                            </div>
                          </div>
                        )}
                        {company.leadTime && (
                          <div className="col-6 col-md-4">
                            <div className="d-flex align-items-center gap-2">
                              <i className="feather-calendar text-muted" style={{ fontSize: 14 }}></i>
                              <div>
                                <div style={{ fontSize: 11 }} className="text-muted">{t('marketplace.provider.leadTime', 'Ledtid')}</div>
                                <div className="fw-semibold small">{company.leadTime}</div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Company Presentation */}
                  {company.companyPresentation ? (
                    <div className="border rounded p-3 bg-white">
                      <small className="fw-semibold text-muted d-block mb-2">{t('settings.companyDetails.companyPresentation', 'Företagspresentation')}</small>
                      <div className="ql-editor p-0" dangerouslySetInnerHTML={{ __html: company.companyPresentation }} />
                    </div>
                  ) : (
                    <p className="text-muted mb-0">{t('personHome.noPresentation', 'Ingen presentation tillagd ännu.')}</p>
                  )}
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* KYC (Kundkännedom) Tab — read-only admin view, no "Starta kundkännedom" */}
      {activeTab === 'kyc' && (
        <Card>
          <KycStatusPanel
            account={adyenAccount}
            loading={adyenLoading}
            message={adyenMessage}
            showStartButton={false}
            showRefreshButton
            onRefresh={() => company && loadAdyenAccount(company.id, { refresh: true })}
            isRefreshing={adyenRefreshing}
            showOpenAdyen={false}
          />
        </Card>
      )}

      {/* Theme (logo and contract colours), same editor as Inställningar → Tema; audited. */}
      {activeTab === 'theme' && company && (
        <Card>
          <div className="card-body">
            <CompanyThemeEditor api="admin" companyId={company.id} />
          </div>
        </Card>
      )}
    </DashboardLayout>
  );
}
